/**
 * Get an order number: Supplier (with LUX's account number) → Project → "Get order
 * number". The number is issued by the server only at that moment.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { useFormDraft } from '../../hooks/useFormDraft';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Loader2, Check, Hash, Search, Store, PenLine } from 'lucide-react';
import { jobCode, isConstruction } from '../../utils/jobCode';
import { AccountNumberCard, useOrderSuppliers } from '../../components/portal/orders/SupplierAccount';

export function PortalOrderCreate() {
  const { api, features } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [projects, setProjects] = useState(null);
  const suppliers = useOrderSuppliers(api);
  const [form, setForm, clearDraft] = useFormDraft('purchase-order', { project_id: params.get('project') || '', supplier_name: '', supplier_id: null, other: false, notes: '' });
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');

  useEffect(() => { api.get('/portal/orders/projects').then(r => setProjects(r.data || [])).catch(e => { setProjects([]); toast.error(e.response?.data?.detail || 'Could not load projects'); }); }, [api]);

  const project = useMemo(() => projects?.find(p => p.id === form.project_id), [projects, form.project_id]);
  const supplier = useMemo(() => suppliers.find(s => s.id === form.supplier_id) || null, [suppliers, form.supplier_id]);
  const company = project ? (isConstruction(project) ? 'LC' : 'LD') : null;
  const shown = useMemo(() => (projects || []).filter(p => !q || `${p.project_code} ${p.project_ref} ${p.address} ${p.postcode}`.toLowerCase().includes(q.toLowerCase())), [projects, q]);
  const supplierOk = form.other ? form.supplier_name.trim().length >= 2 : !!supplier;

  const raise = async () => {
    setBusy(true);
    try {
      const r = await api.post('/portal/orders', { project_id: form.project_id, supplier_name: form.other ? form.supplier_name.trim() : supplier.name, supplier_id: form.other ? null : supplier.id, notes: form.notes || null });
      clearDraft();
      toast.success(`Order number ${r.data.number}`);
      navigate(`/portal/orders/${r.data.id}`, { replace: true });
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === 'object' ? det.message : det || 'Could not raise the order', { duration: 8000 });
    } finally { setBusy(false); }
  };

  return (
    <div className="p-4 space-y-4" data-testid="order-create">
      <section className="space-y-2">
        <h2 className="text-white font-semibold">1. Supplier</h2>
        <div className="space-y-1.5" data-testid="order-supplier-options">
          {suppliers.map(s => (
            <button key={s.id} type="button" onClick={() => setForm(f => ({ ...f, supplier_id: s.id, supplier_name: s.name, other: false }))} className={`w-full text-left rounded-xl border p-3 flex items-center gap-3 active:scale-[0.99] ${!form.other && form.supplier_id === s.id ? 'border-[#F5A623] bg-[#F5A623]/10' : 'border-white/10 bg-[#2A3240]'}`} data-testid={`order-supplier-pick-${s.id}`}>
              <Store className="w-4 h-4 text-[#F5A623] shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="block text-white font-semibold text-sm">{s.name}</span>
                <span className="block text-[11px] text-gray-400 font-mono"><span className="text-blue-300">LC {s.account_lc || '—'}</span> · <span className="text-[#F5A623]">LD {s.account_ld || '—'}</span></span>
              </span>
              {!form.other && form.supplier_id === s.id && <Check className="w-4 h-4 text-[#F5A623]" />}
            </button>
          ))}
          <button type="button" onClick={() => setForm(f => ({ ...f, other: true, supplier_id: null, supplier_name: f.other ? f.supplier_name : '' }))} className={`w-full text-left rounded-xl border p-3 flex items-center gap-3 active:scale-[0.99] ${form.other ? 'border-[#F5A623] bg-[#F5A623]/10' : 'border-dashed border-white/15 bg-transparent'}`} data-testid="order-supplier-other">
            <PenLine className="w-4 h-4 text-gray-400 shrink-0" />
            <span className="text-sm text-gray-300">Other supplier…</span>
          </button>
          {form.other && <Input value={form.supplier_name} onChange={e => setForm(f => ({ ...f, supplier_name: e.target.value }))} placeholder="Supplier name" className="h-12 bg-[#2A3240] border-white/20 text-white" data-testid="order-supplier" />}
        </div>
        <AccountNumberCard supplier={supplier} company={company} />
      </section>

      <section className="space-y-2">
        <h2 className="text-white font-semibold">2. Which project?</h2>
        {projects && projects.length > 6 && <div className="relative"><Search className="w-4 h-4 text-gray-500 absolute left-3 top-3.5" /><Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search job or address" className="h-11 pl-9 bg-[#2A3240] border-white/20 text-white" data-testid="order-project-search" /></div>}
        {projects === null ? <div className="py-4 text-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" /></div> : projects.length === 0 ? (
          <div className="text-sm text-gray-400 rounded-xl border border-dashed border-white/15 p-4 text-center" data-testid="order-no-projects">You're not on any projects yet.</div>
        ) : (
          <div className="space-y-1.5 max-h-56 overflow-y-auto pr-0.5" data-testid="order-project-options">
            {shown.map(p => (
              <button key={p.id} type="button" onClick={() => setForm(f => ({ ...f, project_id: p.id }))} className={`w-full text-left rounded-xl border p-3 flex items-center gap-3 active:scale-[0.99] ${form.project_id === p.id ? 'border-[#F5A623] bg-[#F5A623]/10' : 'border-white/10 bg-[#2A3240]'}`} data-testid={`order-project-${p.id}`}>
                <span className={`font-bold text-sm w-16 shrink-0 ${isConstruction(p) ? 'text-blue-400' : 'text-[#F5A623]'}`}>{jobCode(p, features)}</span>
                <span className="flex-1 min-w-0"><span className="block text-white text-sm truncate">{p.address || p.project_ref}</span>{p.postcode && <span className="block text-[11px] text-gray-500">{p.postcode}</span>}</span>
                {form.project_id === p.id && <Check className="w-4 h-4 text-[#F5A623]" />}
              </button>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-white font-semibold">3. What's it for? <span className="text-gray-500 text-sm font-normal">(optional)</span></h2>
        <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="e.g. 20 bags cement, 2 × 4.8m 6x2" className="bg-[#2A3240] border-white/20 text-white min-h-[70px]" data-testid="order-notes" />
      </section>

      <Button onClick={raise} disabled={busy || !form.project_id || !supplierOk} className="w-full h-14 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-bold text-base" data-testid="order-get-number-btn">
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Hash className="w-5 h-5 mr-2" /> Get order number{project ? ` for ${jobCode(project, features)}` : ''}</>}
      </Button>
      <p className="text-[11px] text-gray-500 text-center">The number is issued when you tap — quote it with the account number above and it appears on your Orders list.</p>
    </div>
  );
}
